import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { classAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ProfilePhotoEditor } from '../components/ProfilePhotoEditor';
import { Textarea } from '../components/ui/textarea';
import { Coins, Plus, GraduationCap, Users, ArrowRight, Copy, CheckCircle2, MoreVertical, Archive, ArchiveRestore, Trash2, Eye, EyeOff, Pencil, Link2, Share2, CopyPlus, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { formatDate } from '../lib/utils';
import { stripHtml } from '../components/common/RichTextEditor';

export default function Dashboard() {
  const { user, isProfessor } = useAuth();
  const navigate = useNavigate();
  const [classes, setClasses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [formData, setFormData] = useState({ name: '', semester: '', description: '' });
  const [joinCode, setJoinCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [copiedCode, setCopiedCode] = useState(null);
  const [showArchived, setShowArchived] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [classToDelete, setClassToDelete] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingClass, setEditingClass] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', semester: '', description: '' });

  useEffect(() => {
    loadClasses();
  }, [showArchived]);

  const loadClasses = async () => {
    try {
      const response = await classAPI.list(showArchived);
      setClasses(response.data);
    } catch (error) {
      toast.error('Error al cargar clases');
    } finally {
      setLoading(false);
    }
  };

  const handleArchiveClass = async (classId, e) => {
    e.stopPropagation();
    try {
      await classAPI.archive(classId);
      toast.success('Clase archivada');
      loadClasses();
    } catch (error) {
      toast.error('Error al archivar clase');
    }
  };

  const handleUnarchiveClass = async (classId, e) => {
    e.stopPropagation();
    try {
      await classAPI.unarchive(classId);
      toast.success('Clase restaurada');
      loadClasses();
    } catch (error) {
      toast.error('Error al restaurar clase');
    }
  };

  const handleDeleteClass = async () => {
    if (!classToDelete) return;
    try {
      await classAPI.delete(classToDelete);
      toast.success('Clase eliminada permanentemente');
      setDeleteDialogOpen(false);
      setClassToDelete(null);
      loadClasses();
    } catch (error) {
      toast.error('Error al eliminar clase');
    }
  };

  const openDeleteDialog = (classId, e) => {
    e.stopPropagation();
    setClassToDelete(classId);
    setDeleteDialogOpen(true);
  };

  const openEditDialog = (cls, e) => {
    e.stopPropagation();
    setEditingClass(cls);
    setEditForm({
      name: cls.name,
      semester: cls.semester,
      description: cls.description || ''
    });
    setEditDialogOpen(true);
  };

  const handleCloneClass = async (classId, e) => {
    e.stopPropagation();
    try {
      const res = await classAPI.clone(classId);
      toast.success(res.data.message || 'Clase duplicada exitosamente');
      loadClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al duplicar clase');
    }
  };

  const handleEditClass = async (e) => {
    e.preventDefault();
    if (!editingClass || !editForm.name.trim() || !editForm.semester.trim()) {
      toast.error('Nombre y semestre son requeridos');
      return;
    }
    try {
      await classAPI.update(editingClass.id, editForm);
      toast.success('Clase actualizada');
      setEditDialogOpen(false);
      setEditingClass(null);
      loadClasses();
    } catch (error) {
      toast.error('Error al actualizar clase');
    }
  };

  const handleCreateClass = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await classAPI.create(formData);
      toast.success('¡Clase creada exitosamente!');
      setCreateOpen(false);
      setFormData({ name: '', semester: '', description: '' });
      loadClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear clase');
    } finally {
      setSubmitting(false);
    }
  };

  const handleJoinClass = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await classAPI.join({ join_code: joinCode });
      toast.success('¡Te uniste a la clase!');
      setJoinOpen(false);
      setJoinCode('');
      loadClasses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Código inválido');
    } finally {
      setSubmitting(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    toast.success('Código copiado');
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const copyInviteLink = (joinCode, e) => {
    e?.stopPropagation();
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/join/${joinCode}`;
    navigator.clipboard.writeText(link);
    toast.success('Enlace de invitación copiado');
  };

  const shareInviteLink = async (cls, e) => {
    e?.stopPropagation();
    const baseUrl = window.location.origin;
    const link = `${baseUrl}/join/${cls.join_code}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: `Únete a ${cls.name} en CampusCoins`,
          text: `Tu profesor te ha invitado a la clase "${cls.name}". Haz clic en el enlace para unirte y comenzar a ganar coins y XP.`,
          url: link
        });
      } catch {}
    } else {
      navigator.clipboard.writeText(link);
      toast.success('Enlace copiado');
    }
  };

  return (
    <div>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h2 className="text-xl sm:text-2xl lg:text-3xl font-heading font-bold text-slate-900">
              {isProfessor ? 'Panel de Profesor' : 'Panel de Estudiante'}
            </h2>
            <p className="text-sm text-slate-500 mt-1">
              {isProfessor ? 'Gestiona tus clases y estudiantes' : 'Accede a tus clases y gana coins'}
            </p>
          </div>
          
          <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
            <ProfilePhotoEditor 
              currentPhotoUrl={user?.photo_url} 
              name={user?.name}
              onUpdate={(newPhotoUrl) => {
                const updatedUser = {...user, photo_url: newPhotoUrl};
                localStorage.setItem('user', JSON.stringify(updatedUser));
              }}
            />
            {isProfessor && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowArchived(!showArchived)}
                className={showArchived ? 'bg-slate-100' : ''}
                data-testid="toggle-archived-btn"
              >
                {showArchived ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
                {showArchived ? 'Ocultar Archivadas' : 'Ver Archivadas'}
              </Button>
            )}
            
            {isProfessor ? (
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20" data-testid="create-class-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Nueva Clase
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleCreateClass}>
                  <DialogHeader>
                    <DialogTitle className="font-heading">Crear Nueva Clase</DialogTitle>
                    <DialogDescription>
                      Crea una clase y comparte el código con tus estudiantes
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="name">Nombre de la Clase</Label>
                      <Input
                        id="name"
                        placeholder="Introducción a la Física"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        data-testid="class-name-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="semester">Semestre</Label>
                      <Input
                        id="semester"
                        placeholder="2025-1"
                        value={formData.semester}
                        onChange={(e) => setFormData({...formData, semester: e.target.value})}
                        required
                        data-testid="class-semester-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="description">Descripción (opcional)</Label>
                      <Input
                        id="description"
                        placeholder="Descripción breve de la clase"
                        value={formData.description}
                        onChange={(e) => setFormData({...formData, description: e.target.value})}
                        data-testid="class-description-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={submitting} data-testid="submit-create-class">
                      {submitting ? 'Creando...' : 'Crear Clase'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          ) : (
            <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700 shadow-lg shadow-blue-500/20" data-testid="join-class-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Unirse a Clase
                </Button>
              </DialogTrigger>
              <DialogContent>
                <form onSubmit={handleJoinClass}>
                  <DialogHeader>
                    <DialogTitle className="font-heading">Unirse a una Clase</DialogTitle>
                    <DialogDescription>
                      Ingresa el código que te proporcionó tu profesor
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label htmlFor="joinCode">Código de Clase</Label>
                      <Input
                        id="joinCode"
                        placeholder="ABC123"
                        value={joinCode}
                        onChange={(e) => setJoinCode(e.target.value.toUpperCase())}
                        required
                        className="text-center text-2xl font-mono tracking-widest"
                        maxLength={6}
                        data-testid="join-code-input"
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button type="submit" disabled={submitting} data-testid="submit-join-class">
                      {submitting ? 'Uniéndose...' : 'Unirse'}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          )}
          </div>
        </div>

        {/* Classes Grid */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <Card key={i} className="border-slate-200/60">
                <CardHeader>
                  <div className="h-6 rounded w-3/4 shimmer" />
                  <div className="h-4 rounded w-1/2 mt-2 shimmer" />
                </CardHeader>
                <CardContent>
                  <div className="h-4 rounded w-full shimmer" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : classes.length === 0 ? (
          <Card className="border-dashed border-2 border-slate-200 bg-white/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
                {isProfessor ? (
                  <GraduationCap className="w-8 h-8 text-slate-400" />
                ) : (
                  <Users className="w-8 h-8 text-slate-400" />
                )}
              </div>
              <h3 className="text-lg font-heading font-semibold text-slate-900 mb-2">
                {isProfessor ? 'No tienes clases creadas' : 'No estás inscrito en ninguna clase'}
              </h3>
              <p className="text-slate-500 text-center max-w-sm mb-4">
                {isProfessor 
                  ? 'Crea tu primera clase y comienza a gamificar el aprendizaje'
                  : 'Pide a tu profesor el código de clase para unirte'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {classes.map((cls) => (
              <Card 
                key={cls.id} 
                className={`group cursor-pointer hover:shadow-lg hover:-translate-y-1 transition-all duration-300 border-slate-200 hover:border-blue-200 ${cls.archived ? 'opacity-60 bg-slate-50' : ''}`}
                onClick={() => !cls.archived && navigate(`/class/${cls.id}`)}
                data-testid={`class-card-${cls.id}`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <CardTitle className={`font-heading text-xl transition-colors ${cls.archived ? 'text-slate-500' : 'group-hover:text-blue-600'}`}>
                          {cls.name}
                        </CardTitle>
                        {cls.archived && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-slate-200 text-slate-600 rounded-full">
                            Archivada
                          </span>
                        )}
                        {cls.is_assistant && (
                          <span className="px-2 py-0.5 text-xs font-medium bg-emerald-100 text-emerald-700 rounded-full flex items-center gap-1" data-testid={`assistant-badge-${cls.id}`}>
                            <ShieldCheck className="w-3 h-3" />
                            Asistente
                          </span>
                        )}
                      </div>
                      <CardDescription className="mt-1">
                        {cls.semester} • {cls.professor_name}
                      </CardDescription>
                    </div>
                    {(isProfessor || cls.is_manager) && cls.is_owner ? (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                          <Button variant="ghost" size="sm" className="h-8 w-8 p-0" data-testid={`class-menu-${cls.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); copyInviteLink(cls.join_code, e); }} data-testid={`invite-link-${cls.id}`}>
                            <Link2 className="w-4 h-4 mr-2" />
                            Copiar Enlace de Invitación
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => shareInviteLink(cls, e)} data-testid={`share-link-${cls.id}`}>
                            <Share2 className="w-4 h-4 mr-2" />
                            Compartir Invitación
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={(e) => openEditDialog(cls, e)}>
                            <Pencil className="w-4 h-4 mr-2" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => handleCloneClass(cls.id, e)} data-testid={`clone-class-${cls.id}`}>
                            <CopyPlus className="w-4 h-4 mr-2" />
                            Duplicar Clase
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {cls.archived ? (
                            <DropdownMenuItem onClick={(e) => handleUnarchiveClass(cls.id, e)}>
                              <ArchiveRestore className="w-4 h-4 mr-2" />
                              Restaurar
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem onClick={(e) => handleArchiveClass(cls.id, e)}>
                              <Archive className="w-4 h-4 mr-2" />
                              Archivar
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={(e) => openDeleteDialog(cls.id, e)}
                            className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Eliminar permanentemente
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    ) : (
                      <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-blue-500 group-hover:translate-x-1 transition-all" />
                    )}
                  </div>
                </CardHeader>
                <CardContent>
                  {cls.description && (
                    <p className="text-sm text-slate-500 mb-4 line-clamp-2">{stripHtml(cls.description)}</p>
                  )}
                  
                  {cls.is_manager && !cls.archived && (
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <span className="text-xs text-slate-500">Código de clase</span>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          copyCode(cls.join_code);
                        }}
                        className="flex items-center gap-1.5 px-2.5 py-1 bg-slate-100 hover:bg-slate-200 rounded-md text-sm font-mono font-medium text-slate-700 transition-colors"
                        data-testid={`copy-code-${cls.id}`}
                      >
                        {copiedCode === cls.join_code ? (
                          <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500" />
                        ) : (
                          <Copy className="w-3.5 h-3.5" />
                        )}
                        {cls.join_code}
                      </button>
                    </div>
                  )}
                  
                  <p className="text-xs text-slate-400 mt-3">
                    Creada {formatDate(cls.created_at)}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-red-600">Eliminar Clase Permanentemente</DialogTitle>
              <DialogDescription>
                Esta acción no se puede deshacer. Se eliminarán todos los datos asociados a la clase: estudiantes, actividades, transacciones, productos, etc.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancelar
              </Button>
              <Button variant="destructive" onClick={handleDeleteClass} data-testid="confirm-delete-class">
                Eliminar Permanentemente
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Class Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <form onSubmit={handleEditClass}>
              <DialogHeader>
                <DialogTitle className="font-heading">Editar Clase</DialogTitle>
                <DialogDescription>Modifica los datos de la clase</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Nombre de la Clase</Label>
                  <Input
                    value={editForm.name}
                    onChange={(e) => setEditForm({...editForm, name: e.target.value})}
                    required
                    data-testid="edit-class-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Semestre</Label>
                  <Input
                    value={editForm.semester}
                    onChange={(e) => setEditForm({...editForm, semester: e.target.value})}
                    required
                    data-testid="edit-class-semester"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Descripción</Label>
                  <Textarea
                    value={editForm.description}
                    onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                    placeholder="Descripción de la clase..."
                    data-testid="edit-class-description"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Cancelar
                </Button>
                <Button type="submit" data-testid="save-edit-class">
                  Guardar Cambios
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  );
}
